import * as React from "react";
import { ComponentType } from "react";
import {Page as MainPagePage} from "./design/templates/MainPageLayout";
import i18n from "./i18n";

import HomeIcon from '@material-ui/icons/Home';
import IndexPage from "./design/pages/IndexPage";

import AssessmentIcon from '@material-ui/icons/Assessment';
import StorageIcon from '@material-ui/icons/Storage';

import { OutreachIcon } from "./design/atoms/icons/OutreachIcon";
import { ContactpointIcon } from "./design/atoms/icons/ContactpointIcon";
import { AutomaticTaskIcon } from "./design/atoms/icons/AutomaticTaskIcon";
import { ClientIcon } from "./design/atoms/icons/ClientIcon";
import { SettingsIcon } from "./design/atoms/icons/SettingsIcon";
import MyOutreach from "./design/pages/outreach/MyOutreach";
import OutreachList from "./design/pages/outreach/OutreachList";
import OutreachDetails from "./design/pages/outreach/OutreachDetails";
import OutreachEditPage from "./design/pages/outreach/OutreachEdit";
import OutreachNew from "./design/pages/outreach/OutreachNew";

import ContactpointList from "./design/pages/contactpoint/ContactpointList";
import ContactpointDetails from "./design/pages/contactpoint/ContactpointDetails";
import ContactpointNew from "./design/pages/contactpoint/ContactpointNew";
import ContactpointEdit from "./design/pages/contactpoint/ContactpointEdit";

import AutomaticTaskList from "./design/pages/automatictask/AutomaticTaskList";
import AutomaticTaskDetails from "./design/pages/automatictask/AutomaticTaskDetails";
import AutomaticTaskEditPage from "./design/pages/automatictask/AutomaticTaskEditPage";
import AutomaticTaskNewPage from "./design/pages/automatictask/AutomaticTaskNewPage";

import ClientList from "./design/pages/client/ClientList";
import ClientDetails from "./design/pages/client/ClientDetails";
import ClientEditPage from "./design/pages/client/ClientEditPage";
import ClientNewPage from "./design/pages/client/ClientNewPage";

import SettingsPage from "./design/pages/settings/SettingsPage";
import VaphTokenPage from "./design/pages/settings/VaphTokenPage";

import {hasAccessLevel, hasSettingsAccess, IData as UserInfoData, UserAccessLevel} from "./store/user/userInfo";


interface Page extends MainPagePage {
    route?: string;
    isExact: boolean;
    // If true, the page will be shown in the menu
    inMenu: boolean;
    // Allow both functional components and connected components
    component?: ComponentType<any>;
    // Optional: function to check if user has permission to access this page
    // Returns true if user has access, false otherwise
    requiredPermission?: (userInfo: UserInfoData) => boolean;
}

export interface Routes {
    baseUrl: string;
    pages: Page[];
}

const defaultPage: Page = {
    inMenu: true,
    isExact: true,
    text: "Default",
    icon: <HomeIcon />,
    url: "/"
}

export const oldRapportenUrl = '/rapporten';
export const databaseUrl = '/database';
export const oldDatabaseUrl = '/api/redirect/oldDatabase';

const routesCalculator = (baseUrl: string, oldRapportenUrl: string, oldDatabaseUrl: string): Routes  => {
    // Helper function to get translations
    const t = (namespace: string, key: string) => i18n.t(key, { ns: namespace });
    
    // Import UserAccessLevel for cleaner code
    const { UserAccessLevel, hasAccessLevel } = require("./store/user/userInfo");
    
    return {
        baseUrl: baseUrl,
        pages: [
            // Main Page
            {
                ...defaultPage,
                text: t('common', 'startTitle'),
                icon: <HomeIcon />,
                url: `${baseUrl}/`,
                component: IndexPage,
            },

            
            // Client Pages
            {
                ...defaultPage,
                text: t('common', 'clientsTitle'),
                icon: <ClientIcon />,
                url: `${baseUrl}/client/`,
                component: ClientList,
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/client/details/:id`,
                component: ClientDetails,
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/client/edit/:id`,
                component: ClientEditPage,
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/client/new`,
                component: ClientNewPage,
            },
            
            
            // Outreach Pages
            {
                ...defaultPage,
                text: t('outreach', 'outreach'),
                icon: <OutreachIcon />,
                url: `${baseUrl}/outreach/`,
                component: MyOutreach,                
            },            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/outreach/list/`,
                component: OutreachList,                
            },         
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/outreach/details/:id`,
                component: OutreachDetails,                
            },         
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/outreach/edit/:id`,
                component: OutreachEditPage,                
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/outreach/new/`,
                component: OutreachNew,                
            },




            // Contactpoint Pages
            {
                ...defaultPage,
                text: t('common', 'contactpointsTitle'),
                icon: <ContactpointIcon />,
                url: `${baseUrl}/contactpoint/`,
                component: ContactpointList,                
            },    
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/contactpoint/details/:id`,
                component: ContactpointDetails,               
            },         
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/contactpoint/edit/:id`,
                component: ContactpointEdit, // Updated to use ContactpointEdit component               
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/contactpoint/new/`,
                component: ContactpointNew,               
            },

            // AutomaticTask Pages
            {
                ...defaultPage,
                text: t('common', 'automaticTasksTitle'),
                icon: <AutomaticTaskIcon />,
                url: `${baseUrl}/automatictask/`,
                component: AutomaticTaskList,
                requiredPermission: (userInfo) => hasAccessLevel(userInfo, UserAccessLevel.AutomaticTasks),
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/automatictask/details/:id`,
                component: AutomaticTaskDetails,
                requiredPermission: (userInfo) => hasAccessLevel(userInfo, UserAccessLevel.AutomaticTasks),
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/automatictask/edit/:id`,
                component: AutomaticTaskEditPage,
                requiredPermission: (userInfo) => hasAccessLevel(userInfo, UserAccessLevel.AutomaticTasks),
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/automatictask/new`,
                component: AutomaticTaskNewPage,
                requiredPermission: (userInfo) => hasAccessLevel(userInfo, UserAccessLevel.AutomaticTasks),
            },




            // Settings Page (protected by permission)
            {
                ...defaultPage,
                text: t('common', 'settingsTitle'),
                icon: <SettingsIcon />,
                url: `${baseUrl}/settings/`,
                component: SettingsPage,
                requiredPermission: (userInfo) => hasSettingsAccess(userInfo),
            },
            {
                ...defaultPage,
                inMenu: false,
                url: `${baseUrl}/settings/vaphtoken`,
                component: VaphTokenPage,
                requiredPermission: (userInfo) => hasAccessLevel(userInfo, UserAccessLevel.VaphTokenManagement),
            },




            // External Pages
            {
                ...defaultPage,
                text: t('common', 'rapportenTitle'),
                icon: <AssessmentIcon />,
                url: `${oldRapportenUrl}/`,
            },
            {
                ...defaultPage,
                text: t('common', 'oudeDatabaseTitle'),
                icon: <StorageIcon />,
                url: `${oldDatabaseUrl}`,
                useWindowLocation: true,
            },
        ],
    };
}

export const routes = routesCalculator(databaseUrl, oldRapportenUrl, oldDatabaseUrl)